<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch, type PropType } from 'vue'
import { BarChartSorting, SolidFill, ColorHEX, emptyLine, FontSettings, AxisTickStrategies, Themes } from '@lightningchart/lcjs'

interface ProjectMaterialConsumptionItem {
  holeNo: number
  maxPressure: number
  cement: number
  additive1: number
  additive2: number
  additive3: number
  accelerator: number
}

const props = defineProps({
  materialOverviewConsumedFromPumps: {
    type: Array as PropType<ProjectMaterialConsumptionItem[]>,
    required: true
  }
});

const fmt = (val: number | undefined) => {
  if (val === undefined || val === null) return '0';
  return val.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const headers = [
  { title: 'Hole No', key: 'holeNo', align: 'start' as const },
  { title: 'Max Pressure (bar)', key: 'maxPressure', align: 'end' as const },
  { title: 'Cement (kg)', key: 'cement', align: 'end' as const },
  { title: 'Accelerator (kg)', key: 'accelerator', align: 'end' as const },
  { title: 'Additive 1 (L)', key: 'additive1', align: 'end' as const },
  { title: 'Additive 2 (L)', key: 'additive2', align: 'end' as const },
  { title: 'Additive 3 (L)', key: 'additive3', align: 'end' as const },
];

// --- Bar chart ---
const chartContainer = ref<HTMLDivElement>()
let chart: any = undefined

const barData = computed(() => {
  if (!props.materialOverviewConsumedFromPumps?.length) return []
  return props.materialOverviewConsumedFromPumps.map(item => ({
    category: `H${item.holeNo}`,
    value: item.cement || 0
  }))
})

const initChart = () => {
  if (!chartContainer.value) return
  const lc = useUseLightningCharts()

  const AMV_BLUE   = ColorHEX('#041e43')
  const TEXT_DARK  = ColorHEX('#1e2d3d')
  const TEXT_MID   = ColorHEX('#4b5e6d')
  const PAGE_BG    = ColorHEX('#f5f5f5')

  const axisLabelFont = new FontSettings().setSize(11).setFamily('Arial').setWeight('normal')
  const titleFont     = new FontSettings().setSize(15).setFamily('Arial').setWeight('bold')

  chart = lc.BarChart({ container: chartContainer.value, theme: Themes.light })
    .setTitle('Total Cement per Hole (kg)')
    .setTitleFont(titleFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_DARK }))
    .setTitleEffect(false)
    .setBackgroundFillStyle(new SolidFill({ color: PAGE_BG }))
    .setBackgroundStrokeStyle(emptyLine)
    .setSeriesBackgroundFillStyle(new SolidFill({ color: ColorHEX('#ffffff') }))
    .setSeriesBackgroundEffect(false)
    .setCornerRadius(3)
    .setBarsMargin(0.05)
    .setBarsEffect(false)
    .setAnimationsEnabled(false)
    .setSorting(BarChartSorting.Disabled)
    .setValueLabels(undefined)
    .setCategoryLabels({
      labelFont: axisLabelFont,
      labelFillStyle: new SolidFill({ color: TEXT_MID })
    })

  chart.valueAxis
    .setTickStrategy(AxisTickStrategies.Numeric, (ticks: any) =>
      ticks.setMajorTickStyle((major: any) =>
        major
          .setLabelFont(axisLabelFont)
          .setLabelFillStyle(new SolidFill({ color: TEXT_MID }))
      )
    )
    .setTitle('kg')
    .setTitleFont(axisLabelFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_MID }))

  if (barData.value.length > 0) {
    applyChartData(barData.value)
  }
}

onMounted(() => {
  initChart()
})

onUnmounted(() => {
  chart?.dispose()
  chart = undefined
})

const applyChartData = (data: { category: string; value: number }[]) => {
  chart.setData(data)
  chart.getSeries().forEach((bar: any) => {
    bar.setFillStyle(new SolidFill({ color: ColorHEX('#041e43') }))
       .setStrokeStyle(emptyLine)
  })
}

watch(barData, (newData) => {
  if (!chart || !newData.length) return
  applyChartData(newData)
})
</script>

<template>
  <v-container fluid>
    <!-- Bar Chart -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
        Consumption Overview
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <div ref="chartContainer" style="height: 350px; width: 100%;"></div>
      </v-card-text>
    </v-card>

    <!-- Detailed Table -->
    <v-card elevation="2">
      <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
        <span class="text-subtitle-1 font-weight-bold">Consumption per Hole</span>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="props.materialOverviewConsumedFromPumps"
        density="compact"
        class="text-body-2"
        hover
      >
        <template v-slot:item.holeNo="{ item }">
          <v-chip size="small" variant="flat" color="blue-lighten-5" class="text-blue-darken-2 font-weight-bold">
            {{ item.holeNo }}
          </v-chip>
        </template>

        <template v-slot:item.maxPressure="{ item }"><span class="font-monospace">{{ fmt(item.maxPressure) }}</span></template>
        <template v-slot:item.cement="{ item }"><span class="font-monospace">{{ fmt(item.cement) }}</span></template>
        <template v-slot:item.accelerator="{ item }"><span class="font-monospace">{{ fmt(item.accelerator) }}</span></template>
        <template v-slot:item.additive1="{ item }"><span class="font-monospace">{{ fmt(item.additive1) }}</span></template>
        <template v-slot:item.additive2="{ item }"><span class="font-monospace">{{ fmt(item.additive2) }}</span></template>
        <template v-slot:item.additive3="{ item }"><span class="font-monospace">{{ fmt(item.additive3) }}</span></template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>
